// context/permissionsContext.js
import React, { createContext, useState, useEffect, useMemo, useContext } from 'react';
import { apiClient } from '../config/apiClient.js';
import { LoginContext } from './loginContext.js';

export const PermissionsContext = createContext();

/**
 * Proveedor de permisos. Carga los permisos del usuario al iniciar sesión.
 * Expone: permissions (array de códigos o ['*']), loading, hasPermission(modulo, accion).
 */
export const PermissionsProvider = ({ children }) => {
  const { user, isAuthenticated } = useContext(LoginContext);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const isAdmin =
    user?.tiu_descripcion?.toUpperCase() === 'ADMINISTRADOR' ||
    user?.usu_tipousuario === 1;

  useEffect(() => {
    if (!isAuthenticated || !user || !isAdmin) {
      setPermissions([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    apiClient
      .get('/gestion/usuarios/me/permissions')
      .then((res) => {
        if (cancelled) return;
        if (res.ok) return res.json();
        return { permissions: [] };
      })
      .then((data) => {
        if (cancelled) return;
        setPermissions(Array.isArray(data?.permissions) ? data.permissions : []);
      })
      .catch(() => {
        if (!cancelled) setPermissions([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [isAuthenticated, user?.id, user?.usu_id, isAdmin]);

  const hasPermission = useMemo(() => {
    return (modulo, accion) => {
      if (!Array.isArray(permissions)) return false;
      if (permissions.includes('*')) return true;
      const code = accion ? `${modulo}.${accion}` : modulo;
      return permissions.includes(code);
    };
  }, [permissions]);

  const RUTAS_GESTION_ORDEN = useMemo(() => [
    ['dashboard', 'ver', '/gestion/dashboard'],
    ['ventas', 'ver', '/gestion/ventas'],
    ['compras', 'ver', '/gestion/compras'],
    ['gastos', 'ver', '/gestion/gastos'],
    ['cobros', 'ver', '/gestion/cobros'],
    ['cuenta_corriente', 'ver', '/gestion/cuenta-corriente'],
    ['caja_diaria', 'ver', '/gestion/caja-diaria'],
    ['rendicion_caja', 'ver', '/gestion/rendicion-caja'],
    ['articulos', 'ver', '/gestion/movimientos-stock'],
    ['usuarios', 'ver', '/gestion/usuarios'],
    ['articulos', 'ver', '/gestion/articulos'],
  ], []);

  const getFirstGestionPath = useMemo(() => {
    return () => {
      for (const [modulo, accion, path] of RUTAS_GESTION_ORDEN) {
        if (hasPermission(modulo, accion)) return path;
      }
      return '/gestion/dashboard';
    };
  }, [hasPermission, RUTAS_GESTION_ORDEN]);

  const value = useMemo(
    () => ({ permissions, loading, hasPermission, getFirstGestionPath }),
    [permissions, loading, hasPermission, getFirstGestionPath]
  );

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissions = () => {
  const ctx = useContext(PermissionsContext);
  if (!ctx) throw new Error('usePermissions debe usarse dentro de PermissionsProvider');
  return ctx;
};
